import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Image as ImageIcon, 
  Palette, 
  BookOpen, 
  Printer,
  Award,
  Clock,
  Star,
  ArrowRight,
  Check,
  Package,
  Shirt
} from 'lucide-react';
import NotebookCarousel from '../components/NotebookCarousel';

const merchandisingCarouselImages = [
  { src: '/assets/notebooks/IMG_1698.jpg', alt: 'Carnet premium embossage or' },
  { src: '/assets/notebooks/IMG_1699.jpg', alt: 'Carnet corporate spirale latérale' },
  { src: '/assets/notebooks/IMG_1704.jpg', alt: 'Notebook couverture totem floral' },
  { src: '/assets/notebooks/IMG_1705.jpg', alt: 'Notebook édition limitée rose' },
  { src: '/assets/notebooks/IMG_1706.jpg', alt: 'Carnet artisan reliure couture apparente' },
  { src: '/assets/notebooks/IMG_1707.jpg', alt: 'Carnet de notes impression UV' },
  { src: '/assets/notebooks/IMG_1709.jpg', alt: 'Carnet premium marquage blanc' },
  { src: '/assets/notebooks/IMG_1713.jpg', alt: 'Notebook créatif palette pastel' },
];

const Services = () => {
  const services = [
    {
      id: 7,
      icon: <Shirt className="w-12 h-12" />,
      title: "Merchandising & accessoires à la demande",
      shortDescription: "Capsules lifestyle pour équipes créatives",
      longDescription: "Créez instantanément une collection aux couleurs de votre marque : gourdes thermo, t-shirts premium, tote bags, casquettes ou patchs. Nous gérons la personnalisation à la pièce, sans stock à financer.",
      features: [
        "Impression textile DTG, sérigraphie HQ et broderie 3D",
        "Gourdes isothermes gravées laser ou impression UV 360°",
        "Personnalisation individuelle (prénoms, hashtags, QR codes)",
        "Catalogue d'accessoires éco-friendly (bambou, coton bio, RPET)",
        "Portail de commande à la demande pour vos équipes ou fans"
      ],
      examples: [
        "T-shirts oversize édition limitée pour staff",
        "Packs onboarding (gourde + hoodie + carnet)",
        "Merch événementiel pour festivals ou hackathons"
      ],
      image: "https://public.youware.com/image/94e08b36-8ef5-4546-9fe0-a886d0c493af/340n3fh5po.jpg",
      badge: "Nouveauté 2025",
      isYouthFocus: true,
      ctaLabel: "Lancer ma collection",
      whatsappLabel: "Brief WhatsApp"
    },
    {
      id: 1,
      icon: <FileText className="w-12 h-12" />,
      title: "Cartes de visite",
      shortDescription: "Cartes professionnelles de qualité premium",
      longDescription: "Créez une première impression mémorable avec nos cartes de visite haut de gamme. Nous utilisons des papiers de qualité supérieure et des techniques d'impression avancées.",
      features: [
        "Papier premium 350g/m²",
        "Finitions pelliculage mat ou brillant",
        "Découpe précise aux angles arrondis",
        "Impression recto-verso incluse",
        "Délai express 24h disponible"
      ],
      examples: [
        "Cartes standard avec logo",
        "Cartes avec effets spéciaux (dorure, vernis sélectif)",
        "Cartes transparentes ou plastiques"
      ],
      image: "https://public.youware.com/image/6f2a28ff-69eb-440b-900c-038acb3b02ee/77yc2t1wo6.jpeg"
    },
    {
      id: 2,
      icon: <ImageIcon className="w-12 h-12" />,
      title: "Flyers & Brochures",
      shortDescription: "Communication visuelle percutante",
      longDescription: "Valorisez votre communication avec nos flyers et brochures. De la conception à l'impression, nous vous accompagnons pour un rendu professionnel.",
      features: [
        "Formats A5, A4, A3 et sur mesure",
        "Papiers couché brillant ou mat",
        "Pliage professionnel inclus",
        "BAT numérique avant impression",
        "Quantités flexibles dès 100 ex"
      ],
      examples: [
        "Flyers publicitaires recto-verso",
        "Brochures corporate 3 volets",
        "Catalogues produits reliés"
      ],
      image: "/brochure-banking.jpg" // Image personnalisée : Brochure institutionnelle "THE SANCTIONS SYSTEM OF THE BANK GROUP" - Format professionnel avec design bleu marine
    },
    {
      id: 3,
      icon: <Palette className="w-12 h-12" />,
      title: "Affiches & Posters",
      shortDescription: "Impression grand format haute résolution",
      longDescription: "Donnez de l'impact à votre communication avec nos affiches grand format. Qualité d'impression exceptionnelle garantie.",
      features: [
        "Formats A3 à A0 et plus",
        "Impression haute résolution 1440 DPI",
        "Papiers photo ou affichage",
        "Plastification disponible",
        "Pose et installation possibles"
      ],
      examples: [
        "Affiches publicitaires extérieur",
        "Posters décoratifs intérieur",
        "Panneaux de communication événementielle"
      ],
      image: "/assets/services/poster-section.png"
    },
    {
      id: 4,
      icon: <BookOpen className="w-12 h-12" />,
      title: "Reliure & Finition",
      shortDescription: "Finitions professionnelles et reliure",
      longDescription: "Complétez vos impressions avec nos services de reliure et finition. Du simple agrafage à la reliure spirale ou thermocollée.",
      features: [
        "Reliure spirale plastique ou métal",
        "Reliure thermocollée dos carré",
        "Agrafage coin et 2 points",
        "Perforation et œilletage",
        "Plastification et pelliculage"
      ],
      examples: [
        "Rapports d'activité reliés",
        "Manuels de formation spiralés",
        "Catalogues dos carré collé"
      ],
      image: "https://cdn.pixabay.com/photo/2018/10/08/17/41/printing-3733124_640.jpg"
    },
    {
      id: 5,
      icon: <Printer className="w-12 h-12" />,
      title: "Impression Offset",
      shortDescription: "Qualité professionnelle pour gros tirages",
      longDescription: "Pour vos gros tirages, l'impression offset reste la référence qualité. Couleurs fidèles et rendu parfait garantis.",
      features: [
        "Quadrichromie CMJN",
        "Couleurs Pantone sur demande",
        "Papiers de 80g à 400g",
        "Tirages minimum 500 exemplaires",
        "Contrôle qualité rigoureux"
      ],
      examples: [
        "Magazines et revues",
        "Livres et ouvrages",
        "Packaging et étiquettes"
      ],
      image: "https://cdn.pixabay.com/photo/2015/05/27/20/50/printing-787192_640.jpg"
    },
    {
      id: 6,
      icon: <Award className="w-12 h-12" />,
      title: "Impression Numérique",
      shortDescription: "Flexibilité et rapidité pour petits tirages",
      longDescription: "L'impression numérique offre une solution rapide et économique pour vos petits tirages avec une qualité remarquable.",
      features: [
        "Tirages à partir de 1 exemplaire",
        "Délais courts (24-48h)",
        "Données variables possibles",
        "Couleurs vives et contrastées",
        "Finitions multiples"
      ],
      examples: [
        "Tirages de plans et documents",
        "Personnalisation et données variables",
        "Prototypes et échantillons"
      ],
      image: "https://public.youware.com/users-website-assets/prod/8a85bc5f-95a8-48f3-a4c8-51f1ad983885/d752a81ea94e442d94bb698e0646e404.jpeg"
    }
  ];

  const testimonials = [
    {
      name: "Marie-Claire Kouassi",
      company: "Directrice Marketing, Société IVOIRE+",
      service: "Brochures et cartes de visite",
      text: "Excellent travail sur nos supports de communication. La qualité d'impression est remarquable et les délais ont été respectés à la lettre.",
      rating: 5
    },
    {
      name: "Amadou Traoré",
      company: "Gérant, Restaurant Maquis du Bonheur",
      service: "Menus et flyers",
      text: "DigiPrint a transformé l'image de mon restaurant avec des menus de qualité professionnelle. Mes clients sont impressionnés !",
      rating: 5
    },
    {
      name: "Esther Diabaté",
      company: "Fondatrice, ONG ESPOIR CI",
      service: "Rapports et documentation",
      text: "Service impeccable pour l'impression de nos rapports annuels. L'équipe comprend nos besoins et propose toujours des solutions adaptées.",
      rating: 5
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Consultation",
      description: "Échanges sur vos besoins et conseils personnalisés"
    },
    {
      step: 2,
      title: "Devis gratuit",
      description: "Proposition tarifaire détaillée sous 24h"
    },
    {
      step: 3,
      title: "Validation BAT",
      description: "Bon à tirer numérique pour validation finale"
    },
    {
      step: 4,
      title: "Production",
      description: "Impression avec contrôle qualité rigoureux"
    },
    {
      step: 5,
      title: "Livraison",
      description: "Livraison rapide ou retrait en atelier"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gray-50 py-10" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        <div className="container mx-auto px-4 text-center">
          <h1 
            className="font-bold mb-6 text-center"
            style={{ 
              color: '#1A1A1A', 
              fontSize: 'clamp(24px, 4vw, 44px)', 
              fontWeight: 700, 
              lineHeight: 1.2,
              textShadow: '0px 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            Nos Services d'Impression Professionnelle
          </h1>
          <p 
            className="mb-8 mx-auto text-center"
            style={{ 
              color: '#333333', 
              fontSize: 'clamp(16px, 2vw, 18px)', 
              fontWeight: 400, 
              lineHeight: 1.6,
              maxWidth: '850px'
            }}
          >
            De l'impression offset traditionnelle au numérique moderne, découvrez notre gamme complète 
            de services pour tous vos besoins d'impression à Abidjan.
          </p>
          <Link
            to="/contact"
            className="bg-primary-pink hover:bg-primary-pink-dark text-white px-8 py-4 rounded-lg font-medium transition-colors inline-flex items-center space-x-2"
          >
            <span>Demander un devis gratuit</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      {/* Section Galerie Visuelle - Grille 2x2 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="section-tag">Notre Savoir-Faire</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              L'Art du Numérique en Images
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez la qualité exceptionnelle de nos réalisations à travers notre portfolio visuel
            </p>
          </div>

          {/* Grille 2x2 élégante */}
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {/* Image 1: Carte de visite (haut gauche) */}
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 p-2">
                  <img
                    src="https://public.youware.com/image/169a1e95-7e7f-4c73-8a1f-1f29e5d31a78/vgvj0gfx3r.jpeg"
                    alt="Cartes de visite professionnelles - DigiPrint Abidjan"
                    className="w-full h-full object-cover rounded-xl border border-gray-200"
                    style={{objectFit: 'cover'}}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-2xl">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold mb-1">Cartes de visite</h3>
                    <p className="text-sm text-gray-200">Qualité premium</p>
                  </div>
                </div>
              </div>

              {/* Image 2: Brochure (haut droite) - Avec Upload */}
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 p-2">
                  <img
                    id="brochure-image"
                    src="/brochure-banking.jpg" // Image intégrée : Brochure01.jpeg - "THE SANCTIONS SYSTEM OF THE BANK GROUP"
                    alt="Brochures et flyers professionnels - DigiPrint Abidjan"
                    className="w-full h-full object-cover rounded-xl border border-gray-200"
                    style={{objectFit: 'cover'}}
                  />
                </div>
                
                {/* Zone d'upload overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-2xl">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold mb-1">Brochures & Flyers</h3>
                    <p className="text-sm text-gray-200">Design professionnel</p>
                  </div>
                  

                </div>
              </div>

              {/* Case 3: Affiches & Posters (bas gauche) - Avec Upload */}
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 p-2">
                  <img
                    id="poster-image"
                    src="/assets/services/poster-section.png"
                    alt="Affiches et posters grand format - DigiPrint Abidjan"
                    className="w-full h-full object-cover rounded-xl border border-gray-200"
                    style={{objectFit: 'cover'}}
                  />
                </div>
                
                {/* Zone d'upload overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-2xl">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold mb-1">Affiches & Posters</h3>
                    <p className="text-sm text-gray-200">Grand format</p>
                  </div>
                  

                </div>
              </div>

              {/* Case 4: Reliure & Finition (bas droite) - Avec Upload */}
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 p-2">
                  <img
                    id="binding-image"
                    src="/brochure-calendar.jpg" // Image intégrée : Brochure02.jpeg - Calendrier à reliure spirale avec carte de Côte d'Ivoire
                    alt="Reliure et finition professionnelles - DigiPrint Abidjan"
                    className="w-full h-full object-cover rounded-xl border border-gray-200"
                    style={{objectFit: 'cover'}}
                  />
                </div>
                
                {/* Zone d'upload overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-2xl">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold mb-1">Reliure & Finition</h3>
                    <p className="text-sm text-gray-200">Services complets</p>
                  </div>
                  

                </div>
              </div>
            </div>

            {/* Call to action sous la grille */}
            <div className="text-center mt-12">
              <p className="text-gray-600 mb-6">
                Découvrez tous nos services d'impression professionnelle
              </p>
              <Link
                to="/gallery"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Voir notre portfolio complet</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Services détaillés */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {services.map((service, index) => {
            const isYouthFocus = service.isYouthFocus;
            const cardBackground = isYouthFocus
              ? 'relative mb-20 bg-white rounded-3xl p-10 shadow-2xl border border-pink-100 overflow-hidden'
              : `mb-20 ${index % 2 === 1 ? 'bg-white' : 'bg-gray-50'} rounded-2xl p-8`;
            const iconWrapperClass = isYouthFocus
              ? 'bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 w-20 h-20 rounded-3xl flex items-center justify-center text-white mb-6 shadow-lg'
              : 'bg-blue-100 w-20 h-20 rounded-2xl flex items-center justify-center text-blue-600 mb-6';
            const contentWrapperClass = `${isYouthFocus ? 'relative z-10' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`;
            const infoColumnOrder = isYouthFocus ? 'order-1' : index % 2 === 1 ? 'order-2' : 'order-1';
            const mediaColumnOrder = isYouthFocus ? 'order-2' : index % 2 === 1 ? 'order-1' : 'order-2';

            return (
              <div key={service.id} className={cardBackground}>
                {isYouthFocus && (
                  <div
                    className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_left,#E91E63,transparent_45%),radial-gradient(circle_at_bottom_right,#AD1457,transparent_55%)]"
                    aria-hidden="true"
                  />
                )}
                <div className={contentWrapperClass}>
                  <div className={infoColumnOrder}>
                    {service.badge && (
                      <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-4 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                        {service.badge}
                      </div>
                    )}
                    <div className={iconWrapperClass}>
                      {service.icon}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{service.title}</h2>
                    {service.shortDescription && (
                      <p className="text-sm uppercase tracking-[0.3em] text-gray-400 mb-4">{service.shortDescription}</p>
                    )}
                    <p className={`text-lg mb-6 ${isYouthFocus ? 'text-gray-700' : 'text-gray-600'}`}>{service.longDescription}</p>
                    
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Caractéristiques :</h3>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <li key={i} className="flex items-start space-x-2">
                            <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isYouthFocus ? 'text-pink-500' : 'text-green-500'}`} />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Exemples de réalisations :</h3>
                      <ul className="space-y-1">
                        {service.examples.map((example, i) => (
                          <li key={i} className="text-gray-600">• {example}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/contact"
                        className={`${isYouthFocus ? 'bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 hover:from-pink-600 hover:via-rose-600 hover:to-orange-500' : 'bg-blue-600 hover:bg-blue-700'} text-white px-6 py-3 rounded-lg transition-colors font-medium text-center`}
                      >
                        {service.ctaLabel || 'Commander ce service'}
                      </Link>
                      <a
                        href="https://wa.me/2250787635341"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${isYouthFocus ? 'bg-gray-900 hover:bg-black' : 'bg-green-500 hover:bg-green-600'} text-white px-6 py-3 rounded-lg transition-colors font-medium text-center`}
                      >
                        {service.whatsappLabel || 'WhatsApp'}
                      </a>
                    </div>
                  </div>
                  
                  <div className={mediaColumnOrder}>
                    <div className={isYouthFocus ? 'relative group' : ''}>
                      {isYouthFocus && (
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-pink-500 to-orange-400 rounded-full opacity-20 blur-3xl transition-transform group-hover:scale-110" />
                      )}
                      {service.isYouthFocus ? (
                        <NotebookCarousel
                          images={merchandisingCarouselImages}
                          autoPlayDelay={4000}
                          className="h-full max-w-full"
                        />
                      ) : (
                        <img
                          src={service.image}
                          alt={`${service.title} - DigiPrint Abidjan`}
                          className="w-full rounded-2xl shadow-2xl"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Processus de Travail
            </h2>
            <p className="text-xl text-gray-600">
              5 étapes simples pour des résultats parfaits
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0">
            {processSteps.map((process, index) => (
              <div key={index} className="text-center flex-1 max-w-xs">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-8 transform translate-x-8">
                    <ArrowRight className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Témoignages Clients
            </h2>
            <p className="text-xl text-gray-600">
              La satisfaction de nos clients parle pour nous
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <div className="bg-blue-50 text-blue-600 text-sm px-3 py-1 rounded-full inline-block mb-3">
                  {testimonial.service}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                  <p className="text-gray-500 text-sm">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à démarrer votre projet d'impression ?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-medium transition-colors"
            >
              Demander un devis
            </Link>
            <a
              href="https://wa.me/2250787635341"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-medium transition-colors"
            >
              WhatsApp Direct
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;